import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useVideo } from "../../context/VideoContext";
import { useSelectedSubtitles } from "../../context/SelectedSubtitlesContext";
import useEmbeddedSubtitles from "../../hooks/useEmbeddedSubtitles";
import { parseSRTFileWithEncoding } from "../../utils/subtitleEncoding";
import { rankSubtitles } from "../../utils/SubtitleRankingEngine";
import { isEmbeddedCacheValid, setEmbeddedCache, getEmbeddedCache } from "../../utils/embeddedSubtitlesCache";
import ComparisonSubtitleSearch from "./ComparisonSubtitleSearch";
import WatchReferenceModal from "./WatchReferenceModal";
import WatchTwoSubtitlesModal from "./WatchTwoSubtitlesModal";
import { getPortalContainer } from "../../utils/portalContainer";
import "./SubtitleComparisonWizard.css";

const LANGUAGE_NAMES = {
  eng: "English", en: "English",
  fas: "فارسی", per: "فارسی", fa: "فارسی",
  ara: "العربية", tur: "Türkçe", tr: "Türkçe",
  deu: "Deutsch", ger: "Deutsch", de: "Deutsch",
  fre: "Français", fra: "Français", fr: "Français",
  ita: "Italiano", it: "Italiano",
  spa: "Español", es: "Español",
  rus: "Русский", ru: "Русский",
  chi: "中文", zho: "中文", zh: "中文",
  jpn: "日本語", ja: "日本語",
  kor: "한국어", ko: "한국어",
  und: "Unknown",
};

const getLangName = (code) => {
  if (!code) return "Unknown";
  return LANGUAGE_NAMES[code.toLowerCase()] || code;
};

const timeToSeconds = (t) => {
  if (!t) return 0;
  const p = t.split(/[:,.]/);
  return p.length >= 3 ? parseInt(p[0]) * 3600 + parseInt(p[1]) * 60 + parseInt(p[2]) + (p[3] ? parseInt(p[3]) / 1000 : 0) : 0;
};

const SubtitleComparisonWizard = ({ isOpen, onClose }) => {
  const {
    videoRef,
    videoMetadata, videoFile,
    subtitle1Json, subtitle2Json,
    isFullscreen, isIOS,
    isPlaying, setIsPlaying,
  } = useVideo();
  const { activeSubtitles } = useSelectedSubtitles();
  const { getEmbeddedSubtitles, extractSubtitleContent } = useEmbeddedSubtitles();

  // --- General state ---
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);

  // --- Step 2: First round selection ---
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [allSubtitles, setAllSubtitles] = useState([]);
  const [embeddedList, setEmbeddedList] = useState([]);
  const [isScanningEmbedded, setIsScanningEmbedded] = useState(false);
  const [embeddedError, setEmbeddedError] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [accordionState, setAccordionState] = useState({ embedded: true, online: true, file: true, active: true });
  const fileInputRef = useRef(null);

  // --- Step 3: Reference comparison ---
  const [referenceId, setReferenceId] = useState(null);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);

  // --- Step 4: Second round selection ---
  const [secondRoundIds, setSecondRoundIds] = useState(new Set());
  const [secondRoundSubs, setSecondRoundSubs] = useState([]);
  const [accordionStateS4, setAccordionStateS4] = useState({ embedded: true, online: true, file: true, active: true });
  const [showSearchModalS4, setShowSearchModalS4] = useState(false);
  const [showWatchModal, setShowWatchModal] = useState(false);

  // --- Step 5: Ranking ---
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");

  // --- Step 6: Ranking results ---
  const [rankedResults, setRankedResults] = useState([]);
  const [rankedSelectedId, setRankedSelectedId] = useState(null);
  const [showTwoWatchModal, setShowTwoWatchModal] = useState(false);

  // --- Playback speed ---
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const videoFileName = videoFile?.name || videoMetadata?.fileName || "";
  const progressBarRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [hoverTime, setHoverTime] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef({ isDragging: false });

  // --- Init embedded cache on open ---
  useEffect(() => {
    if (!isOpen) return;
    if (isEmbeddedCacheValid(videoFileName)) {
      setEmbeddedList(getEmbeddedCache().list);
    } else {
      setEmbeddedList([]);
    }
  }, [isOpen, videoFileName]);

  // --- Reset on close ---
  useEffect(() => {
    if (!isOpen) {
      setStep(1); setSelectedIds(new Set()); setAllSubtitles([]);
      setSecondRoundIds(new Set()); setSecondRoundSubs([]);
      setReferenceId(null); setError(null); setIsProcessing(false);
      setRankedResults([]); setRankedSelectedId(null);
      setShowTwoWatchModal(false); setPlaybackSpeed(1);
    }
  }, [isOpen]);

  // --- Video time tracking ---
  useEffect(() => {
    if (!videoRef.current || (step !== 3 && step !== 6)) return;
    const video = videoRef.current;
    if (video.duration) setDuration(video.duration);
    setCurrentVideoTime(video.currentTime);
    const onTime = () => { if (!dragStateRef.current.isDragging) setCurrentVideoTime(video.currentTime); };
    const onMeta = () => setDuration(video.duration || 0);
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("loadedmetadata", onMeta);
    return () => { video.removeEventListener("timeupdate", onTime); video.removeEventListener("loadedmetadata", onMeta); };
  }, [videoRef, step]);

  // --- Helpers ---
  const getActiveCue = useCallback((cues) => {
    if (!cues || cues.length === 0) return null;
    const t = currentVideoTime;
    return cues.find((cue) => {
      const s = timeToSeconds(cue.startTime);
      const e = timeToSeconds(cue.endTime);
      return t >= s && t <= e;
    }) || null;
  }, [currentVideoTime]);

  const toggleAccordion = (key, s4 = false) => {
    if (s4) setAccordionStateS4((p) => ({ ...p, [key]: !p[key] }));
    else setAccordionState((p) => ({ ...p, [key]: !p[key] }));
  };

  const addSub = useCallback((sub, list, setList, setIdSet) => {
    const exists = list.find((s) => s.id === sub.id);
    if (exists) { setList((p) => p.map((s) => (s.id === sub.id ? { ...s, ...sub } : s))); return; }
    setList((prev) => [...prev, sub]);
    setIdSet((prev) => new Set([...prev, sub.id]));
  }, []);

  const addSubtitle = useCallback((sub) => {
    addSub(sub, allSubtitles, setAllSubtitles, setSelectedIds);
  }, [addSub, allSubtitles]);

  const addSubtitleS4 = useCallback((sub) => {
    addSub(sub, secondRoundSubs, setSecondRoundSubs, setSecondRoundIds);
  }, [addSub, secondRoundSubs]);

  // --- Embedded scan ---
  const handleScanEmbedded = async () => {
    if (!videoFile) { setEmbeddedError("فایل ویدیو در دسترس نیست"); return; }
    setIsScanningEmbedded(true); setEmbeddedError(null);
    try {
      const subs = await getEmbeddedSubtitles(videoFile);
      const mapped = subs.map((sub, i) => ({ ...sub, id: sub.id || `embedded-${i}`, tempId: `embedded-${i}`, trackIndex: i, source: "embedded" }));
      setEmbeddedList(mapped);
      setEmbeddedCache(mapped, videoFileName);
    } catch { setEmbeddedError("خطا در شناسایی زیرنویس‌های داخلی"); }
    finally { setIsScanningEmbedded(false); }
  };

  const extractEmbedded = async (sub, addFn) => {
    if (!videoFile) return;
    setIsExtracting(true);
    try {
      const content = await extractSubtitleContent(videoFile, sub.trackIndex);
      const srtParser2 = (await import("srt-parser-2")).default;
      const parser = new srtParser2();
      const cues = parser.fromSrt(content);
      addFn({ id: sub.id, source: "embedded", language: sub.language, title: `${getLangName(sub.language)} (داخلی)`, cues, fileName: `embedded_track_${sub.trackIndex}.srt`, trackIndex: sub.trackIndex });
    } catch { setError("خطا در استخراج زیرنویس"); }
    finally { setIsExtracting(false); }
  };

  const handleSelectEmbedded = (sub) => extractEmbedded(sub, addSubtitle);
  const handleSelectEmbeddedS4 = (sub) => extractEmbedded(sub, addSubtitleS4);

  // --- Online search result ---
  const handleOnlineSubtitle = useCallback((newSub) => {
    const id = newSub.id || `online-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
    addSubtitle({ ...newSub, id, source: "online", title: newSub.title || newSub.nameOfMovie || "زیرنویس آنلاین" });
  }, [addSubtitle]);

  const handleOnlineSubtitleS4 = useCallback((newSub) => {
    const id = newSub.id || `online-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
    addSubtitleS4({ ...newSub, id, source: "online", title: newSub.title || newSub.nameOfMovie || "زیرنویس آنلاین" });
  }, [addSubtitleS4]);

  // --- File upload ---
  const handleFileUpload = async (e, addFn) => {
    const files = Array.from(e.target.files).slice(0, 15);
    setError(null);
    for (const file of files) {
      try {
        const cues = await parseSRTFileWithEncoding(file);
        const id = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        addFn({ id, source: "file", language: "unknown", title: file.name, cues, fileName: file.name });
      } catch { setError(`خطا در پردازش فایل ${file.name}`); }
    }
    e.target.value = "";
  };

  // --- Active subs ---
  const getActiveSubs = useCallback(() => {
    const subs = [];
    if (subtitle1Json?.length > 0) {
      const m = activeSubtitles?.subtitle1;
      subs.push({ id: m?.id || "active-1", source: "active", language: m?.language || m?.subtitlesLanguage || "en", title: m?.nameOfMovie ? `زیرنویس ۱ - ${m.nameOfMovie}` : "زیرنویس فعال ۱", cues: subtitle1Json, fileName: m?.fileName || "active_1.srt" });
    }
    if (subtitle2Json?.length > 0) {
      const m = activeSubtitles?.subtitle2;
      subs.push({ id: m?.id || "active-2", source: "active", language: m?.language || m?.subtitlesLanguage || "fa", title: m?.nameOfMovie ? `زیرنویس ۲ - ${m.nameOfMovie}` : "زیرنویس فعال ۲", cues: subtitle2Json, fileName: m?.fileName || "active_2.srt" });
    }
    return subs;
  }, [subtitle1Json, subtitle2Json, activeSubtitles]);
  const activeSubs = getActiveSubs();

  // --- Toggle handlers ---
  const toggleSelect = (id, ids, setIds) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleToggleSubtitle = (id) => toggleSelect(id, selectedIds, setSelectedIds);
  const handleToggleSubtitleS4 = (id) => {
    if (secondRoundIds.size >= 15 && !secondRoundIds.has(id)) return;
    toggleSelect(id, secondRoundIds, setSecondRoundIds);
  };

  // --- Render subtitle item ---
  const renderSubItem = (sub, isSelected, onToggle, maxReached) => (
    <div key={sub.id} className="scw-sub-item">
      <div className="scw-sub-item-info">
        <span className="scw-sub-lang">{getLangName(sub.language)}</span>
        <span className="scw-sub-title" title={sub.title}>{sub.title}</span>
        {sub.cues?.length > 0 && <span className="scw-sub-meta">{sub.cues.length} دیالوگ</span>}
      </div>
      <button className={`scw-sub-toggle ${isSelected ? "selected" : "unselected"}`}
        onClick={() => onToggle(sub.id)}
        disabled={!isSelected && maxReached}>
        {isSelected ? "حذف" : "انتخاب"}
      </button>
    </div>
  );

  const getItems = (source, list) => list.filter((s) => s.source === source);
  const onlineItems = getItems("online", allSubtitles);
  const fileItems = getItems("file", allSubtitles);
  const onlineItemsS4 = getItems("online", secondRoundSubs);
  const fileItemsS4 = getItems("file", secondRoundSubs);

  // --- Accordion section builder ---
  const renderAccordion = (sectionKey, label, items, list, addFn, embeddedFn, scanFn, scanning, s4 = false) => {
    const state = s4 ? accordionStateS4 : accordionState;
    const isOpen = state[sectionKey];
    const toggle = () => toggleAccordion(sectionKey, s4);
    const ids = s4 ? secondRoundIds : selectedIds;
    const setIds = s4 ? setSecondRoundIds : setSelectedIds;
    const maxReached = ids.size >= 15;

    return (
      <div className="scw-accordion-section">
        <div className="scw-accordion-header" onClick={toggle}>
          <div className="scw-accordion-header-left">
            <span className={`scw-accordion-icon ${isOpen ? "open" : "closed"}`}>▶</span>
            <span className="scw-accordion-label">{label}</span>
            {items.length > 0 && <span className="scw-accordion-badge">{items.length}</span>}
          </div>
          <span className={`scw-accordion-toggle-icon ${isOpen ? "open" : "closed"}`}>▼</span>
        </div>
        <div className={`scw-accordion-body ${isOpen ? "open" : "closed"}`}>
          <div className="scw-accordion-content">
            {sectionKey === "embedded" && items.length === 0 && !scanning ? (
              <div className="scw-embedded-load">
                <p className="scw-embedded-load-text">برای گرفتن لیست زیرنویس ها کلیک کنید</p>
                <button className="scw-embedded-load-btn" onClick={scanFn} disabled={scanning || !videoFile}>
                  {scanning ? "در حال بررسی..." : "دریافت لیست زیرنویس‌ها"}
                </button>
              </div>
            ) : sectionKey === "embedded" && scanning ? (
              <div className="scw-empty">در حال بررسی زیرنویس‌های داخلی...</div>
            ) : sectionKey === "embedded" && items.length > 0 ? (
              embeddedList.map((sub) => {
                const fullSub = list.find((s) => s.id === sub.id);
                const extracted = !!fullSub?.cues;
                const isSel = ids.has(sub.id);
                const handleClick = () => {
                  if (extracted) { toggleSelect(sub.id, ids, setIds); }
                  else { embeddedFn(sub); }
                };
                return (
                  <div key={sub.id} className="scw-sub-item">
                    <div className="scw-sub-item-info">
                      <span className="scw-sub-lang">{getLangName(sub.language)}</span>
                      <span className="scw-sub-title">{sub.title || `Track ${sub.trackIndex}`}</span>
                      {extracted && <span className="scw-sub-meta">{fullSub.cues.length} دیالوگ</span>}
                    </div>
                    <button className={`scw-sub-toggle ${isSel ? "selected" : "unselected"}`}
                      onClick={handleClick}
                      disabled={isExtracting || (!isSel && maxReached)}>
                      {isExtracting ? "..." : isSel ? "حذف" : "انتخاب"}
                    </button>
                  </div>
                );
              })
            ) : items.length === 0 ? (
              <div className="scw-empty">
                {sectionKey === "online" ? "برای جستجوی آنلاین از دکمه بالا استفاده کنید" :
                 sectionKey === "file" ? "برای افزودن فایل از دکمه بالا استفاده کنید" :
                 "زیرنویسی در حال نمایش نیست"}
              </div>
            ) : (
              items.map((sub) => renderSubItem(sub, ids.has(sub.id), (id) => toggleSelect(id, ids, setIds), maxReached))
            )}
          </div>
        </div>
      </div>
    );
  };

  // --- Selection accordion helpers ---
  const renderFullAccordion = (s4 = false) => {
    const items = s4 ? { online: onlineItemsS4, file: fileItemsS4 } : { online: onlineItems, file: fileItems };
    const list = s4 ? secondRoundSubs : allSubtitles;
    const addFn = s4 ? addSubtitleS4 : addSubtitle;
    const embeddedFn = s4 ? handleSelectEmbeddedS4 : handleSelectEmbedded;

    return (
      <div className="scw-accordion">
        {renderAccordion("embedded", "زیرنویسهای چسبیده", embeddedList, list, addFn, embeddedFn, handleScanEmbedded, isScanningEmbedded, s4)}
        {renderAccordion("online", "زیرنویسهای آنلاین", items.online, list, addFn, embeddedFn, null, false, s4)}
        {renderAccordion("file", "زیرنویسهای فایل", items.file, list, addFn, embeddedFn, null, false, s4)}
        {renderAccordion("active", "زیرنویسهای فعال (در حال نمایش)", activeSubs, list, addFn, embeddedFn, null, false, s4)}
      </div>
    );
  };

  // --- Video controls for step 3 ---
  const calcTime = useCallback((clientX) => {
    if (!progressBarRef.current) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    const pos = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(1, pos)) * duration;
  }, [duration]);

  const updateTime = useCallback((t) => { if (videoRef.current) { videoRef.current.currentTime = t; setCurrentVideoTime(t); } }, [videoRef]);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    else { videoRef.current.pause(); setIsPlaying(false); }
  }, [videoRef, setIsPlaying]);

  const handleSpeedChange = (e) => {
    const speed = parseFloat(e.target.value);
    setPlaybackSpeed(speed);
    if (videoRef.current) videoRef.current.playbackRate = speed;
  };

  const progressPercent = duration > 0 ? (currentVideoTime / duration) * 100 : 0;

  if (!isOpen) return null;

  const portalContainer = getPortalContainer(isIOS, isFullscreen);

  const selectCount = selectedIds.size;
  const secondCount = secondRoundIds.size;
  const referenceSub = allSubtitles.find((s) => s.id === referenceId) || activeSubs.find((s) => s.id === referenceId);

  const renderSelectionAccordionTop = (s4 = false) => (
    <div className="scw-top-actions">
      <button className="scw-top-btn" onClick={() => { if (s4) setShowSearchModalS4(true); else setShowSearchModal(true); }}>
        <span>🌐</span> جستجو آنلاین
      </button>
      <button className="scw-top-btn" onClick={() => fileInputRef.current?.click()}>
        <span>📁</span> فایل
      </button>
      <input type="file" accept=".srt,.ass,.ssa,.vtt" multiple ref={fileInputRef} style={{ display: "none" }}
        onChange={(e) => handleFileUpload(e, s4 ? addSubtitleS4 : addSubtitle)} />
    </div>
  );

  return ReactDOM.createPortal(
    <div className="scw-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="scw-modal" onClick={(e) => e.stopPropagation()}>

        {/* ===== HEADER ===== */}
        <div className="scw-header">
          {step === 3 && (
            <button className="scw-btn scw-btn-secondary" style={{ padding: "6px 12px", fontSize: "12px", marginLeft: "8px" }} onClick={() => setStep(2)}>
              ← برگشت
            </button>
          )}
          {step === 4 && (
            <button className="scw-btn scw-btn-secondary" style={{ padding: "6px 12px", fontSize: "12px", marginLeft: "8px" }} onClick={() => setStep(3)}>
              ← برگشت
            </button>
          )}
          {step === 6 && (
            <button className="scw-btn scw-btn-secondary" style={{ padding: "6px 12px", fontSize: "12px", marginLeft: "8px" }} onClick={() => { setStep(4); setRankedResults([]); setRankedSelectedId(null); }}>
              ← برگشت
            </button>
          )}
          <h3>
            {step === 1 && "مقایسه و انتخاب زیرنویس"}
            {step === 2 && "انتخاب زیرنویس مرجع"}
            {step === 3 && `مقایسه زیرنویس‌ها`}
            {step === 4 && "انتخاب زیرنویس دوم"}
            {step === 5 && "در حال پردازش..."}
            {step === 6 && "مقایسه زیرنویس‌های رتبه‌بندی شده"}
          </h3>
          <div className="scw-header-actions">
            {step !== 3 && step !== 4 && step !== 6 && (
              <button className="scw-close-btn" onClick={onClose}>✕</button>
            )}
          </div>
        </div>

        {/* ===== ERROR ===== */}
        {error && (
          <div className="scw-error">
            <span>{error}</span>
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* ======================================================================== */}
        {/* STEP 1: INTRO */}
        {/* ======================================================================== */}
        {step === 1 && (
          <>
            <div className="scw-scrollable">
              <div className="scw-intro">
                <p className="scw-intro-text">با استفاده این سرویس شما می توانید زیرنویس های مختلف را بر روی فیلم قرار دهید و در نهایت حداکثر دو زیرنویس را به عنوان زیرنویس انتخاب کرده و به فیلم اضافه کنید.</p>
                <p className="scw-intro-title">مراحل استفاده از سرویس مقایسه و انتخاب زیرنویس</p>
                <ol className="scw-intro-steps">
                  <li>انتخاب زیرنویس ها از یک زبان (انگلیسی یا فارسی) از بین زیرنویس های چسبیده، فایل و آنلاین</li>
                  <li>مقایسه زیرنویس ها و انتخاب یک زیرنویس هماهنگ با فیلم بعنوان زیرنویس مرجع (زیرنویسهایی که انتخاب کرده اید بر روی فیلم قرار می گیرد و در نهایت شما یک زیرنویس را بعنوان زیرنویس مرجع انتخاب میکنید)</li>
                  <li>انتخاب مجموعه ای از زیرنویس ها با زبان جدید</li>
                  <li>رتبه بندی اتوماتیک بر اساس با زیرنویس مرجع</li>
                  <li>مقایسه زیرنویس ها و انتخاب یک زیرنویس بعنوان زیرنویس دوم (زیرنویسهایی انتخاب کرده اید بر روی فیلم قرار می گیرد و از طریق مقایسه با زیرنویس مرجع شما یک زیرنویس را بعنوان زیرنویس دوم انتخاب میکنید)</li>
                  <li>تعیین شماره و زبان زیرنویس ها و درنهایت افزودن به فیلم</li>
                </ol>
              </div>
            </div>
            <div className="scw-footer">
              <button className="scw-btn scw-btn-primary" onClick={() => { setStep(2); setError(null); }}>ادامه</button>
            </div>
          </>
        )}

        {/* ======================================================================== */}
        {/* STEP 2: FIRST SELECTION (ACCORDIONS) */}
        {/* ======================================================================== */}
        {step === 2 && (
          <>
            {renderSelectionAccordionTop(false)}
            {embeddedError && (
              <div className="scw-error"><span>{embeddedError}</span><button onClick={() => setEmbeddedError(null)}>✕</button></div>
            )}
            <div className="scw-scrollable">
              {renderFullAccordion(false)}
            </div>
            <div className="scw-footer">
              <button className="scw-btn scw-btn-secondary" onClick={() => setStep(1)}>برگشت</button>
              <span className="scw-footer-info">
                {selectCount > 0 ? `${selectCount} زیرنویس انتخاب شده` : "هیچ زیرنویسی انتخاب نشده"}
              </span>
              {selectCount > 15 && <span className="scw-footer-warning">حداکثر ۱۵ زیرنویس قابل انتخاب است</span>}
              <button className="scw-btn scw-btn-primary"
                disabled={!(selectCount >= 1 && selectCount <= 15)}
                onClick={() => {
                  const selected = allSubtitles.filter((s) => selectedIds.has(s.id));
                  const activeSelected = activeSubs.filter((s) => selectedIds.has(s.id));
                  const merged = [...selected, ...activeSelected.filter((a) => !selected.find((s) => s.id === a.id))];
                  const unique = merged.filter((s, i, a) => a.findIndex((x) => x.id === s.id) === i);
                  setAllSubtitles(unique);
                  setSelectedIds(new Set(unique.map((s) => s.id)));
                  setStep(3);
                }}>
                انتخاب {selectCount} زیرنویس و شروع مقایسه
              </button>
            </div>
          </>
        )}

        {/* ======================================================================== */}
        {/* STEP 3 & 6: COMPARISON ROWS with checkbox, subtitle text, video controls */}
        {/* ======================================================================== */}
        {(step === 3 || step === 6) && (
          <>
            <div className="scw-scrollable">
              <div className={step === 6 ? "scw-rank-list" : "scw-compare-list"}>
                {(() => {
                  let items;
                  let readOnlyId;
                  let selectedId;
                  let onSelect;
                  let onRemove;
                  let renderLabel;

                  if (step === 3) {
                    items = allSubtitles;
                    selectedId = referenceId;
                    onSelect = (id) => setReferenceId((prev) => prev === id ? null : id);
                    onRemove = (id) => {
                      setAllSubtitles((p) => p.filter((s) => s.id !== id));
                      setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
                      if (referenceId === id) setReferenceId(null);
                    };
                    renderLabel = null;
                  } else {
                    items = rankedResults;
                    readOnlyId = referenceSub?.id;
                    selectedId = rankedSelectedId;
                    onSelect = (id) => setRankedSelectedId((prev) => prev === id ? null : id);
                    onRemove = null;
                    renderLabel = (sub, i) => {
                      if (sub.id === readOnlyId) return <span className="scw-crow-status">مرجع</span>;
                      const rank = i;
                      const score = sub._score ? (sub._score * 100).toFixed(0) : "?";
                      return <span className="scw-crow-score">#{rank} - {score}%</span>;
                    };
                  }

                  if (!items || items.length === 0) {
                    return <div className="scw-empty" style={{ padding: "40px" }}>هیچ زیرنویسی وجود ندارد</div>;
                  }

                  return items.map((sub, idx) => {
                    const activeCue = getActiveCue(sub.cues);
                    const isChecked = step === 3 ? (selectedId === sub.id) : (selectedId === sub.id);
                    const isReadOnly = sub.id === readOnlyId;
                    const sourceLabel = sub.source === "embedded" ? "داخلی" : sub.source === "online" ? "آنلاین" : sub.source === "file" ? "فایل" : "فعال";

                    return (
                      <div key={sub.id}
                        className={`scw-crow ${isChecked ? "checked" : ""} ${isReadOnly ? "reference-row" : ""}`}>
                        <div className={`scw-crow-check ${isReadOnly ? "disabled" : ""}`}
                          onClick={() => { if (!isReadOnly) onSelect(sub.id); }}>
                          {isChecked && "✓"}
                        </div>
                        {(step === 3 && !isReadOnly) && (
                          <button className="scw-crow-remove-btn" onClick={() => onRemove(sub.id)}
                            title="حذف زیرنویس">✕</button>
                        )}
                        <span className="scw-crow-lang">{getLangName(sub.language)}</span>
                        <span className="scw-crow-source-badge">{sourceLabel}</span>
                        <div className={`scw-crow-text ${!activeCue ? "empty" : ""}`}
                          title={activeCue?.text || ""}>
                          {activeCue ? activeCue.text : "---"}
                        </div>
                        {renderLabel && renderLabel(sub, idx)}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div className="scw-progress-mini">
              <div className="scw-progress-mini-bar" ref={progressBarRef}
                onClick={(e) => { const t = calcTime(e.clientX); updateTime(t); }}
                onMouseDown={(e) => { dragStateRef.current.isDragging = true; setIsDragging(true); updateTime(calcTime(e.clientX)); }}
                onMouseMove={(e) => { if (dragStateRef.current.isDragging) updateTime(calcTime(e.clientX)); else setHoverTime(calcTime(e.clientX)); }}
                onTouchStart={(e) => { const t = calcTime(e.touches[0].clientX); dragStateRef.current.isDragging = true; setIsDragging(true); updateTime(t); }}
                onTouchMove={(e) => { if (dragStateRef.current.isDragging) updateTime(calcTime(e.touches[0].clientX)); }}
                onTouchEnd={() => { dragStateRef.current.isDragging = false; setIsDragging(false); }}
                onMouseUp={() => { dragStateRef.current.isDragging = false; setIsDragging(false); }}
                onMouseLeave={() => { setHoverTime(null); dragStateRef.current.isDragging = false; setIsDragging(false); }}>
                <div className="scw-progress-mini-fill" style={{ width: `${progressPercent}%` }} />
              </div>
              <div className="scw-progress-mini-controls">
                <button onClick={togglePlay}>{isPlaying ? "⏸" : "▶"}</button>
                <select className="scw-speed-select" value={playbackSpeed} onChange={handleSpeedChange}>
                  <option value="0.75">0.75×</option>
                  <option value="0.8">0.8×</option>
                  <option value="0.9">0.9×</option>
                  <option value="0.95">0.95×</option>
                  <option value="1">1×</option>
                  <option value="1.1">1.1×</option>
                  <option value="1.2">1.2×</option>
                </select>
                <span>{Math.floor(currentVideoTime / 60)}:{String(Math.floor(currentVideoTime % 60)).padStart(2, "0")} / {Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, "0")}</span>
              </div>
            </div>

            <div className="scw-footer">
              <span className="scw-footer-info">
                {step === 3
                  ? (referenceId ? "1 زیرنویس انتخاب شده" : "هیچ زیرنویسی انتخاب نشده")
                  : (rankedSelectedId ? "1 زیرنویس انتخاب شده" : "هیچ زیرنویسی انتخاب نشده")}
              </span>
              {step === 3 && (
                <button className="scw-btn scw-btn-primary"
                  disabled={!referenceId}
                  onClick={() => setStep(4)}>
                  انتخاب و ادامه
                </button>
              )}
              {step === 6 && (
                <button className="scw-btn scw-btn-primary"
                  disabled={!rankedSelectedId}
                  onClick={() => {
                    const sel = rankedResults.find((s) => s.id === rankedSelectedId);
                    if (sel) setShowTwoWatchModal(true);
                  }}>
                  انتخاب و ادامه
                </button>
              )}
            </div>
          </>
        )}

        {/* ======================================================================== */}
        {/* STEP 4: SECOND SUBTITLE SELECTION */}
        {/* ======================================================================== */}
        {step === 4 && (
          <>
            {renderSelectionAccordionTop(true)}
            {embeddedError && (
              <div className="scw-error"><span>{embeddedError}</span><button onClick={() => setEmbeddedError(null)}>✕</button></div>
            )}
            <div className="scw-scrollable">
              {renderFullAccordion(true)}
            </div>
            <div className="scw-footer" style={{ flexWrap: "wrap" }}>
              <span className="scw-footer-info">
                {secondCount > 0 ? `${secondCount} زیرنویس انتخاب شده` : "هیچ زیرنویسی انتخاب نشده"}
              </span>
              {secondCount > 15 && <span className="scw-footer-warning" style={{ width: "100%" }}>حداکثر ۱۵ زیرنویس قابل انتخاب است</span>}
              <div style={{ display: "flex", gap: "8px", marginRight: "auto" }}>
                <button className="scw-btn scw-btn-secondary"
                  disabled={!referenceSub}
                  onClick={() => setShowWatchModal(true)}>
                  تماشای فیلم با زیرنویس مرجع
                </button>
                <button className="scw-btn scw-btn-primary"
                  disabled={!(secondCount >= 1 && secondCount <= 15)}
                  onClick={async () => {
                    setIsProcessing(true);
                    setProcessingMessage("در حال انجام پردازش...");
                    setError(null);
                    try {
                      await new Promise((r) => setTimeout(r, 100));
                      const selected = secondRoundSubs.filter((s) => secondRoundIds.has(s.id));
                      const activeSelected = activeSubs.filter((s) => secondRoundIds.has(s.id));
                      const merged = [...selected, ...activeSelected.filter((a) => !selected.find((s) => s.id === a.id))];
                      const unique = merged.filter((s, i, a) => a.findIndex((x) => x.id === s.id) === i);
                      const refCues = referenceSub?.cues;
                      if (!refCues || refCues.length === 0) { setError("زیرنویس مرجع فاقد داده زمانی است"); setIsProcessing(false); return; }
                      const results = rankSubtitles(refCues, unique);
                      const ranked = unique.map((s) => {
                        const r = results.find((x) => x.subtitle?.id === s.id);
                        return { ...s, _score: r ? r.finalScore : 0 };
                      }).sort((a, b) => (b._score || 0) - (a._score || 0));
                      setRankedResults([referenceSub, ...ranked]);
                      setRankedSelectedId(null);
                      setProcessingMessage("");
                      setIsProcessing(false);
                      setStep(6);
                    } catch (err) {
                      setError("خطا در پردازش: " + err.message);
                      setIsProcessing(false);
                    }
                  }}>
                  انتخاب {secondCount} زیرنویس و ادامه مقایسه
                </button>
              </div>
            </div>
          </>
        )}

        {/* ======================================================================== */}
        {/* STEP 5: PROCESSING */}
        {/* ======================================================================== */}
        {step === 5 && (
          <div className="scw-scrollable" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px" }}>
            <div style={{ textAlign: "center" }}>
              <div className="scw-spinner"></div>
              <p style={{ marginTop: "16px", color: "var(--text-secondary)", fontSize: "14px" }}>{processingMessage || "در حال انجام پردازش..."}</p>
            </div>
          </div>
        )}

        {/* ===== ONLINE SEARCH MODALS ===== */}
        {showSearchModal && (
          <ComparisonSubtitleSearch isOpen={showSearchModal} onClose={() => setShowSearchModal(false)} onSelect={handleOnlineSubtitle} defaultLanguage="en" />
        )}
        {showSearchModalS4 && (
          <ComparisonSubtitleSearch
            isOpen={showSearchModalS4}
            onClose={() => setShowSearchModalS4(false)}
            onSelect={handleOnlineSubtitleS4}
            defaultLanguage="fa"
          />
        )}

        {/* ===== WATCH REFERENCE MODAL ===== */}
        {showWatchModal && referenceSub && (
          <WatchReferenceModal
            isOpen={showWatchModal}
            onClose={() => setShowWatchModal(false)}
            referenceSubtitle={referenceSub}
            onAddToPlayer={(sub, lang, num) => {
              setShowWatchModal(false);
              onClose({ watchOnly: true, subtitle: sub, language: lang, targetNumber: num });
            }}
          />
        )}

        {/* ===== WATCH TWO SUBTITLES MODAL ===== */}
        {showTwoWatchModal && referenceSub && (() => {
          const selSub = rankedResults.find((s) => s.id === rankedSelectedId);
          return selSub ? (
            <WatchTwoSubtitlesModal
              isOpen={showTwoWatchModal}
              onClose={() => setShowTwoWatchModal(false)}
              referenceSubtitle={referenceSub}
              selectedSubtitle={selSub}
              onAddToPlayer={(refSub, selSub, lang1, lang2) => {
                setShowTwoWatchModal(false);
                onClose({ watchTwo: true, subtitle1: refSub, subtitle2: selSub, language1: lang1, language2: lang2 });
              }}
            />
          ) : null;
        })()}
      </div>
    </div>,
    portalContainer,
  );
};

export default SubtitleComparisonWizard;
